import supabaseOperations from "../dbOperations";
import rxdbOperations from "../dbOperationsRxDB";
import { useUserStore } from "src/stores/userStore";
import exception from "@/utils/exception";
import Helper, { toDeepRaw } from "@/utils/helper";

/**
 * Configuration object for database operations
 * @typedef {Object} DBConfig
 * @property {string} tableName - The name of the table to operate on
 * @property {boolean} [fetchDeleted=false] - Whether to include soft-deleted records
 * @property {string|null} [searchTxt=null] - Text to search across all columns
 * @property {string|string[]|null} [sortField=null] - Column(s) to sort by
 * @property {number|number[]} [sortOrder=0] - Direction to sort
 * @property {number} [page=0] - Zero-based page number for pagination
 * @property {number} [pageSize=10] - Number of records per page
 * @property {boolean} [fetchAll=false] - If true, ignores pagination
 * @property {Array<{field: string, value: any, opType?: string}>} [queryParams=[]] - Advanced filtering parameters
 * @property {boolean} [headOnly=false] - When true, only returns count
 */

/**
 * Inserts data into the appropriate database based on online status
 * @param {string} tableName - The name of the table
 * @param {Object} obj - The data to insert
 * @returns {Promise<{error: string|null, id: string|null}>}
 */
async function insertData(tableName, obj, offlineFirst = true) {
  let cleanedObj = toDeepRaw(obj);

  // console.log("insertData obj", obj);
  // adding created_by and updated_by fields to the object if not present
  cleanedObj = {
    ...cleanedObj,
    created_by: Helper.getCurrentUserOid(),
    updated_by: Helper.getCurrentUserOid()
  };

  console.log("insertData cleanedObj >>>>>>>>>", cleanedObj);
  try {
    const userStore = useUserStore();
    if (!userStore.isOnline || offlineFirst) {
      // console.log("insertData offlineFirst", cleanedObj);
      return await rxdbOperations.insertData(tableName, cleanedObj);
    } else {
      // console.log("insertData online", cleanedObj );
      return await supabaseOperations.insertData(tableName, cleanedObj);
    }
  } catch (error) {
    exception.catchException(error);
    return {
      error: error.message || "An unexpected error occurred",
      id: null
    };
  }
}

/**
 * Updates data in the appropriate database based on online status
 * @param {string} tableName - The name of the table
 * @param {Object} obj - The data to update
 * @returns {Promise<{error: string|null, data: Object|null}>}
 */
async function updateData(tableName, obj, offlineFirst = true) {
  let cleanedObj = toDeepRaw(obj);
  // adding updated_by field to the object
  cleanedObj = {
    ...cleanedObj,
    updated_by: Helper.getCurrentUserOid()
  };
  try {
    const userStore = useUserStore();
    if (!userStore.isOnline || offlineFirst) {
      return await rxdbOperations.updateData(tableName, cleanedObj);
    } else {
      return await supabaseOperations.updateData(tableName, cleanedObj);
    }
  } catch (error) {
    exception.catchException(error);
    return {
      error: error.message || "An unexpected error occurred",
      data: null
    };
  }
}

/**
 * Fetches data from the appropriate database based on online status
 * @param {DBConfig} config - Configuration object for the fetch operation
 * @returns {Promise<{data: Array|null, totalItems: number, error: string|null}>}
 */
async function fetchData(config, offlineFirst = true) {
  try {
    const userStore = useUserStore();
    const {
      tableName,
      fetchDeleted = false,
      searchTxt = null,
      sortField = null,
      sortOrder = 0,
      page = 0,
      rows: pageSize = 10,
      fetchAll = false,
      queryParams = [],
      headOnly = false
    } = config;

    let _data = null;
    if (!userStore.isOnline || offlineFirst) {
      console.warn(
        `------------- USING RXDB TO FETCH DATA : ${tableName} -------------`
      );
      _data = await rxdbOperations.fetchData(
        tableName,
        fetchDeleted,
        searchTxt,
        sortField,
        sortOrder,
        page,
        pageSize,
        fetchAll,
        queryParams,
        headOnly
      );
    } else {
      console.warn(
        `------------- USING SUPABASE TO FETCH DATA : ${tableName} -------------`
      );
      _data = await supabaseOperations.fetchData(
        tableName,
        fetchDeleted,
        searchTxt,
        sortField,
        sortOrder,
        page,
        pageSize,
        fetchAll,
        queryParams,
        headOnly
      );
    }
    // console.log(`fetchData [${tableName}]`);
    // console.log("config --> :", config);
    // console.log("result --> :", _data);
    return _data;
  } catch (error) {
    exception.catchException(error);
    return {
      data: null,
      totalItems: 0,
      error: error.message || "An unexpected error occurred"
    };
  }
}

/**
 * Gets data by ID from the appropriate database based on online status
 * @param {string} tableName - The name of the table
 * @param {string} id - The ID of the record to fetch
 * @returns {Promise<{error: string|null, data: Object|null, isMasterData: boolean}>}
 */
async function getData(tableName, id, offlineFirst = true) {
  try {
    // console.warn(`----------------- getData -----------------`);
    const userStore = useUserStore();
    // console.log("getData offlineFirst :", offlineFirst);

    let _data = null;
    if (!userStore.isOnline || offlineFirst) {
      console.warn(
        "------------- USING RXDB TO GET DATA ------------- :",
        tableName,
        id
      );
      _data = await rxdbOperations.getData(tableName, id);
    } else {
      console.warn(
        "------------- USING SUPABASE TO GET DATA ------------- :",
        tableName,
        id
      );
      _data = await supabaseOperations.getData(tableName, id);
    }
    // console.log(`getData [${tableName}] : ${id} --> :`, _data);
    return _data;
  } catch (error) {
    exception.catchException(error);
    return {
      error: error.message || "An unexpected error occurred",
      data: null
    };
  }
}

/**
 * Gets the next sequence value from the appropriate database based on online status
 * @param {string} sequenceName - The name of the sequence
 * @returns {Promise<{error: string|null, data: number|null}>}
 */
async function getNextSequenceValue(sequenceName, offlineFirst = true) {
  try {
    const userStore = useUserStore();
    if (!userStore.isOnline || offlineFirst) {
      return await rxdbOperations.getNextSequenceValue(sequenceName);
    } else {
      return await supabaseOperations.getNextSequenceValue(sequenceName);
    }
  } catch (error) {
    exception.catchException(error);
    return {
      error: error.message || "An unexpected error occurred",
      data: null
    };
  }
}

/**
 * Gets distinct column values from the appropriate database based on online status
 * @param {string} tableName - The name of the table
 * @param {string|string[]} columnNames - The column(s) to get distinct values for
 * @returns {Promise<{error: string|null, data: Array|null}>}
 */
async function getDistinctColumnValues(
  tableName,
  columnNames,
  offlineFirst = true
) {
  try {
    const userStore = useUserStore();
    if (!userStore.isOnline || offlineFirst) {
      return await rxdbOperations.getDistinctColumnValues(
        tableName,
        columnNames
      );
    } else {
      return await supabaseOperations.getDistinctColumnValues(
        tableName,
        columnNames
      );
    }
  } catch (error) {
    exception.catchException(error);
    return {
      error: error.message || "An unexpected error occurred",
      data: null
    };
  }
}

/**
 * Uploads a file to storage (only available when online)
 * @param {string} bucketName - The name of the storage bucket
 * @param {string} filePath - The path where the file should be stored
 * @param {File} file - The file to upload
 * @param {Object} [options={}] - Additional upload options
 * @returns {Promise<{error: string|null, data: Object|null}>}
 */
async function uploadToStorage(bucketName, filePath, file, options = {}) {
  try {
    const userStore = useUserStore();
    if (!userStore.isOnline) {
      throw new Error("Cannot upload files while offline");
    }
    return await supabaseOperations.uploadToStorage(
      bucketName,
      filePath,
      file,
      options
    );
  } catch (error) {
    exception.catchException(error);
    return {
      error: error.message || "An unexpected error occurred during upload",
      data: null
    };
  }
}

async function useSupabaseRPC(rpcName, params) {
  try {
    const userStore = useUserStore();
    if (!userStore.isOnline) {
      throw new Error("Cannot use RPC while offline");
    }
    return await supabaseOperations.useSupabaseRPC(rpcName, params);
  } catch (error) {
    exception.catchException(error);
    return {
      error: error.message || "An unexpected error occurred during RPC",
      data: null
    };
  }
}

/**
 * Deletes data from the appropriate database based on online status
 * @param {string} tableName - The name of the table
 * @param {Array<{field: string, value: any, opType?: string}>} queryParams - Query parameters for filtering records to delete
 * @returns {Promise<{error: string|null, data: Object|null}>}
 */
async function deleteData(tableName, queryParams, offlineFirst = true) {
  try {
    const userStore = useUserStore();
    if (!userStore.isOnline || offlineFirst) {
      return await rxdbOperations.deleteData(tableName, queryParams);
    } else {
      return await supabaseOperations.deleteData(tableName, queryParams);
    }
  } catch (error) {
    exception.catchException(error);
    return {
      error: error.message || "An unexpected error occurred during delete",
      data: null
    };
  }
}

/**
 * Subscribe to a document in a collection and add a callback to be called when the document is updated.
 * @param {string} tableName - The name of the collection to subscribe to
 * @param {string} id - The id of the document to subscribe to
 * @param {Function} callback - The callback to be called when the document is updated
 * @returns {Promise<Subscription>}
 */
async function subscribeToDocument(tableName, id, callback) {
  return await rxdbOperations.subscribeToDocument(tableName, id, callback);
}

export default {
  insertData,
  updateData,
  fetchData,
  getData,
  getNextSequenceValue,
  getDistinctColumnValues,
  uploadToStorage,
  deleteData,
  useSupabaseRPC,
  subscribeToDocument
};
